/**
 * WB4.7 — finance reversal / compensating transaction API contract (pilot).
 *
 * POST /api/workspace/finance-reversal-requests
 * GET  /api/workspace/finance-reversal-requests/:requestId
 * POST /api/workspace/finance-reversal-requests/:requestId/post
 * POST /api/workspace/finance-reversal-requests/:requestId/cancel
 */

import { resolveApiOrigin } from '@shared/api/resolve-api-origin';
import type { ReversalCompensationRequest } from '@shared/finance/workbench-reversal-compensation';
import { API_REQUEST_TIMEOUT_MS } from '../config/api';
import { fetchWithAuth } from './authFetch';

const getBaseUrl = (): string => resolveApiOrigin();

export type FinanceReversalRequestRecord = ReversalCompensationRequest;

interface FinanceReversalEnvelope {
  success?: boolean;
  data?: {
    request?: FinanceReversalRequestRecord | null;
  };
}

export async function requestFinanceReversalRemote(input: {
  tenantId: string;
  sourceVoucherId: string;
  objectId: string;
  businessDomain: string;
  streamId?: string;
}): Promise<FinanceReversalRequestRecord | null> {
  try {
    const url = `${getBaseUrl()}/api/workspace/finance-reversal-requests`;
    const res = await fetchWithAuth(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-tenant-id': input.tenantId,
      },
      body: JSON.stringify({
        sourceVoucherId: input.sourceVoucherId,
        objectRef: { id: input.objectId, businessDomain: input.businessDomain },
        ...(input.streamId ? { streamId: input.streamId } : {}),
      }),
      signal: AbortSignal.timeout(API_REQUEST_TIMEOUT_MS),
    });
    const body = (await res.json()) as FinanceReversalEnvelope;
    if (!res.ok || body.success === false || !body.data?.request) {
      return null;
    }
    return body.data.request;
  } catch {
    return null;
  }
}

export async function getFinanceReversalRemote(input: {
  tenantId: string;
  requestId: string;
}): Promise<FinanceReversalRequestRecord | null> {
  const requestId = input.requestId.trim();
  if (!requestId) {
    return null;
  }
  try {
    const url = `${getBaseUrl()}/api/workspace/finance-reversal-requests/${encodeURIComponent(requestId)}`;
    const res = await fetchWithAuth(url, {
      method: 'GET',
      headers: { 'x-tenant-id': input.tenantId },
      signal: AbortSignal.timeout(API_REQUEST_TIMEOUT_MS),
    });
    const body = (await res.json()) as FinanceReversalEnvelope;
    if (!res.ok || body.success === false || !body.data?.request) {
      return null;
    }
    return body.data.request;
  } catch {
    return null;
  }
}

export async function postFinanceReversalRemote(input: {
  tenantId: string;
  requestId: string;
  compensatingVoucherId?: string;
}): Promise<FinanceReversalRequestRecord | null> {
  const requestId = input.requestId.trim();
  if (!requestId) {
    return null;
  }
  try {
    const url = `${getBaseUrl()}/api/workspace/finance-reversal-requests/${encodeURIComponent(requestId)}/post`;
    const res = await fetchWithAuth(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-tenant-id': input.tenantId,
      },
      body: JSON.stringify({
        ...(input.compensatingVoucherId
          ? { compensatingVoucherId: input.compensatingVoucherId }
          : {}),
      }),
      signal: AbortSignal.timeout(API_REQUEST_TIMEOUT_MS),
    });
    const body = (await res.json()) as FinanceReversalEnvelope;
    if (!res.ok || body.success === false || !body.data?.request) {
      return null;
    }
    return body.data.request;
  } catch {
    return null;
  }
}

export async function cancelFinanceReversalRemote(input: {
  tenantId: string;
  requestId: string;
}): Promise<FinanceReversalRequestRecord | null> {
  const requestId = input.requestId.trim();
  if (!requestId) {
    return null;
  }
  try {
    const url = `${getBaseUrl()}/api/workspace/finance-reversal-requests/${encodeURIComponent(requestId)}/cancel`;
    const res = await fetchWithAuth(url, {
      method: 'POST',
      headers: { 'x-tenant-id': input.tenantId },
      signal: AbortSignal.timeout(API_REQUEST_TIMEOUT_MS),
    });
    const body = (await res.json()) as FinanceReversalEnvelope;
    if (!res.ok || body.success === false || !body.data?.request) {
      return null;
    }
    return body.data.request;
  } catch {
    return null;
  }
}
